/**
 * Build a procedural rectangular mirror object for a vanity.
 * Uses the standard dimensions from VANITY_DIMENSIONS.mirror.
 */

const { VANITY_DIMENSIONS } = require('./constants');
const { reSnapToWallPreservingLateralPosition } = require('./placementUtils');

const DEFAULT_HEIGHT_ABOVE_COUNTER = 0.254; // 10" — clears wall faucets

function buildProceduralMirror({
  roomId,
  cabinetId,
  cabinetCenter,
  rotation,
  parentWall,
  parentWallIds,
  countertopTopY,
  fixtureGroupId,
  index = 0,
  totalMirrors = 1,
  addedBy = 'bathroom-post-processing',
  maxWidth = null,
  maxHeight = null,
}) {
  const baseDims = VANITY_DIMENSIONS.mirror;
  const heightAboveCounter =
    baseDims.heightAboveCounter || DEFAULT_HEIGHT_ABOVE_COUNTER;

  // Clamp dimensions to available space so the fallback fits.
  const MIN_MIRROR_WIDTH = 0.3048; // 12"
  const MIN_MIRROR_HEIGHT = 0.3048; // 12"
  let width = baseDims.width;
  let height = baseDims.height;
  if (maxWidth != null && width > maxWidth) {
    width = Math.max(MIN_MIRROR_WIDTH, maxWidth);
  }
  if (maxHeight != null) {
    const availableForMirror = maxHeight - heightAboveCounter;
    if (availableForMirror < height) {
      height = Math.max(MIN_MIRROR_HEIGHT, availableForMirror);
    }
  }

  const mirrorDims = { ...baseDims, width, height };
  const mirrorBottomY = countertopTopY + heightAboveCounter;
  const mirrorCenterY = mirrorBottomY + height / 2;
  const snapDepth = mirrorDims.depth || 0.004;

  let mirrorX = cabinetCenter.x;
  let mirrorZ = cabinetCenter.z;
  if (parentWall) {
    const xz = reSnapToWallPreservingLateralPosition(
      parentWall,
      cabinetCenter,
      snapDepth
    );
    mirrorX = xz.x;
    mirrorZ = xz.z;
  }

  // The id must be unique across every vanity in the room, not just within
  // one vanity's mirror plan. `index` disambiguates the mirrors of a SINGLE
  // vanity; `roomId` is shared by all of them; and `Date.now()` ties only when
  // two vanities are assembled in the same millisecond — which is exactly what
  // happened on structure `C94A2869` (issue #1713), where two vanities both
  // minted `mirror_1779000243697_<roomId>_0` and the pair rendered as one
  // object with an ambiguous `elementIndex` lookup behind it.
  //
  // `cabinetId` is the per-vanity discriminator, so the tie cannot happen at
  // all rather than merely being unlikely. It stays AFTER the `mirror_` prefix
  // because `object.id.split('_')[0]` is a type fallback in the web app
  // (`useObjectUpdates`, `findElementInStructure`).
  const timestamp = Date.now();
  const id = `mirror_${timestamp}_${cabinetId || roomId}_${index}`;

  return {
    id,
    type: 'mirror',
    label: totalMirrors > 1 ? `Mirror ${index + 1}` : 'Mirror',
    category: { mirror: true },
    isProcedural: true,
    modelId: null,
    modelUrl: null,
    modelName: 'Procedural rectangular mirror',
    parentWallIds: parentWallIds || [],
    parents: cabinetId ? [{ id: cabinetId, type: 'lower-cabinet' }] : [],
    center: { x: mirrorX, y: mirrorCenterY, z: mirrorZ },
    dimensions: {
      x: width,
      y: height,
      z: mirrorDims.depth || 0.004,
    },
    rotation: rotation || { x: 0, y: 0, z: 0 },
    fixtureGroupId,
    addedBy,
    lastModified: new Date().toISOString(),
  };
}

module.exports = { buildProceduralMirror };
